import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import type { ProjectId, Tag } from '#shared/types'
import type { Project } from '#shared/projects'
import {
  INCREMENT_HOURS,
  clockAfter,
  formatHours,
  hoursValue,
  minutesBetween,
  minutesToHours,
} from '#shared/duration'
import { formatDateLabel, todayLocal } from '#shared/dates'
import { api, HttpError } from '../api/client'
import { readLogMode, writeLogMode, type LogMode } from '../lib/prefs'
import { LogModeMenu } from './LogModeMenu'
import { ProjectSelect } from './ProjectSelect'
import { TagPicker } from './TagPicker'

const QUICK_HOURS = [0.25, 0.5, 1, 2, 4, 8]

/**
 * Where the start time is put when switching to the time fields with a
 * duration already entered — from a copied shortcut, say.
 *
 * Invented, and harmlessly so: only the duration is ever stored, so this
 * only has to produce a pair of times whose span is the hours already
 * chosen. Nine in the morning is the least surprising place to start.
 */
const DEFAULT_START = '09:00'

/**
 * How long the fields flash after a copy fills them in.
 *
 * Matches `field-flash` in the stylesheet — the class has to come off when
 * the animation ends, or a second copy of the same shortcut would find it
 * already applied and flash nothing.
 */
const FLASH_MS = 1150

interface Props {
  /** The day being logged against — owned by the page, not this form. */
  date: string
  projects: Project[]
  tags: Tag[]
  /**
   * Called after a successful write, and only after one.
   *
   * Handed the project the entry ended up on, which is not always the one
   * the form was opened with: a caller that is showing one project's worth
   * of entries needs to know when what was just written is not one of them.
   */
  onLogged: (projectId: ProjectId) => void | Promise<void>
  /**
   * The project to start on, still changeable.
   *
   * Set when the form is opened from a cell of the week grid: the cell has
   * already answered the question, so the answer is filled in — but a new
   * entry is not bound to the cell it was started from any more than an
   * edit is, and finding the wrong day or the wrong project should not mean
   * closing the dialog and starting again.
   */
  defaultProjectId?: ProjectId
  /** The dialog draws its own heading, and two would be one too many. */
  heading?: boolean
  /**
   * Mark the hours field as the one to focus.
   *
   * A marker rather than the focusing itself: inside a native `<dialog>`,
   * `showModal()` runs after this form has mounted and moves focus to the
   * first focusable element, so anything this form did to itself would be
   * undone a moment later. The dialog focuses the marked field once it has
   * opened — see `CellEntryDialog`.
   */
  autoFocus?: boolean
  /**
   * Draw the eye to this form: take the page to the top and flash the
   * fields and the button.
   *
   * Set where the thing that filled the form in is somewhere else on the
   * page — the Recent Entries card, below the fold — so the press has some
   * visible effect from where it was made. Not set inside a dialog, where
   * the form is the only thing on screen and scrolling the page behind it
   * would be motion with nothing to say.
   */
  announce?: boolean
  /**
   * An existing entry to change, rather than a new one to add.
   *
   * The same form either way, deliberately: the fields, their limits and
   * what counts as valid are identical, and a second form would be a second
   * place for those to drift. Only the verb and the endpoint differ.
   */
  editing?: {
    id: string
    projectId: ProjectId
    minutes: number
    tagIds: string[]
    notes: string | null
  }
  /**
   * Values to start with, for a form that is filling itself in from
   * somewhere else — the Recent Entries card copying a shortcut.
   *
   * Deliberately no date: the day comes from the picker above the form, and
   * the point of copying yesterday's entry is to log it against today.
   */
  initial?: { projectId: ProjectId; tagIds: string[]; minutes: number; notes: string | null }
  /** Shown beside Save while editing. Absent means no way out but saving. */
  onCancel?: () => void
  /**
   * Offered while editing, pushed away from Save and Cancel.
   *
   * Here rather than only on the closed row so that deciding an entry should
   * not exist does not first require backing out of the form.
   */
  onDelete?: () => void
}

/**
 * Project, then any tags that apply, then hours and an optional note.
 *
 * Tags are offered in full whatever project is chosen — they are global, and
 * picking none is an ordinary answer, so there is no rule here that can block
 * a submission the way the old per-project feature requirement could.
 */
export function LogTimeForm({
  date,
  projects,
  tags,
  onLogged,
  defaultProjectId,
  heading = true,
  autoFocus = false,
  announce = false,
  editing,
  initial,
  onCancel,
  onDelete,
}: Props) {
  // `editing` and `initial` never arrive together: one is an entry being
  // changed, the other a blank form pre-filled from one.
  const start = editing ?? initial

  const form = useRef<HTMLFormElement>(null)

  // Two of these forms can be on the page at once — the Dashboard's and a
  // dialog's — so the label needs an id that is unique to this one.
  const projectFieldId = useId()

  /**
   * Set at mount rather than watched, because the page remounts this form
   * on every copy: one mount, one flash, and no state to reset when the
   * same shortcut is copied twice.
   */
  const [flashing, setFlashing] = useState(announce)

  useEffect(() => {
    if (!announce) return

    // Somebody who sets the date, scrolls down and copies wants the picker
    // as much as the fields: the copy deliberately does not bring a date,
    // and the control that decides it is above the form. So: the top of
    // the page, not the top of the form.
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: still ? 'auto' : 'smooth' })

    const timer = window.setTimeout(() => setFlashing(false), FLASH_MS)
    return () => window.clearTimeout(timer)
    // Mount only. `announce` is fixed for the life of this form — the page
    // gives it a new `key` to change it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [projectId, setProjectId] = useState<ProjectId | ''>(
    editing?.projectId ?? initial?.projectId ?? defaultProjectId ?? '',
  )
  const [tagIds, setTagIds] = useState<string[]>(start?.tagIds ?? [])
  const [hours, setHours] = useState(start ? String(minutesToHours(start.minutes)) : '')
  const [notes, setNotes] = useState(start?.notes ?? '')

  /**
   * Which way this person enters a duration. Their choice, remembered in a
   * cookie; read once on mount because it only changes from the menu below.
   */
  const [mode, setMode] = useState<LogMode>(() => readLogMode())

  // The time fields are an input method for `hours`, not a second thing to
  // submit: whatever they say is converted, and only the hours are sent.
  const [from, setFrom] = useState(
    start && readLogMode() === 'range' ? DEFAULT_START : '',
  )
  const [to, setTo] = useState(
    start && readLogMode() === 'range'
      ? clockAfter(DEFAULT_START, start.minutes)
      : '',
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // "Today" needs no restating on the button; an older day does.
  const label = useMemo(() => {
    const text = formatDateLabel(date, todayLocal())
    return text === 'Today' ? '' : text
  }, [date])

  /**
   * What the times come to, or why they do not come to anything yet.
   *
   * Recomputed as they are typed so the person sees the duration they are
   * about to log, and so an unstorable span — 09:00 to 09:50 — is said
   * before the request rather than after it.
   */
  const span = mode === 'range' ? minutesBetween(from, to) : null
  const touched = Boolean(from && to)

  /**
   * Tags chosen for one project do not follow to a project that refuses
   * them: they would be invisible in the form and refused by the API, which
   * is a confusing way to be told no.
   */
  function chooseProject(next: ProjectId) {
    setProjectId(next)
    if (projects.find((p) => p.id === next)?.allowsTags === false) setTagIds([])
  }

  function chooseMode(next: LogMode) {
    setMode(next)
    writeLogMode(next)

    if (next === 'range') {
      // Carry whatever duration is already in the form across, rather than
      // making somebody who copied a shortcut re-enter it.
      const minutes = Number(hours) * 60
      const usable = Number.isFinite(minutes) && minutes > 0
      setFrom(usable ? DEFAULT_START : '')
      setTo(usable ? clockAfter(DEFAULT_START, minutes) : '')
    }
  }

  /** The hours the form will actually submit, whichever way they were given. */
  function durationHours(): string {
    if (mode !== 'range') return hours
    return span?.ok ? hoursValue(span.minutes) : ''
  }

  // The page fetches archived rows too, so historic entries can be named.
  // Only live projects and tags may be logged against.
  const openProjects = useMemo(() => projects.filter((p) => !p.archived), [projects])
  const openTags = useMemo(() => tags.filter((t) => !t.archived), [tags])

  /**
   * Whether this project takes tags at all.
   *
   * Unknown project — nothing chosen yet — counts as yes, so the picker is
   * there from the start rather than appearing once a project is picked.
   */
  const allowsTags = projects.find((p) => p.id === projectId)?.allowsTags ?? true

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    const duration = durationHours()
    // Nothing to send, and nothing to say either: both time fields are
    // `required`, so the only way to get here is a span that does not work
    // — and the field below it is already saying why. Repeating it in the
    // form's error line would print the same sentence twice.
    if (!projectId || !duration) return

    setSaving(true)
    try {
      if (editing) {
        // Every field goes every time, so clearing the note or the last tag
        // is an edit like any other rather than something the form has no
        // way to express.
        await api.updateEntry(date, editing.id, {
          // Sent every time, like the other fields: the form holds the whole
          // entry, and leaving it out when it happens to match would make
          // "unchanged" mean something different here than it does above.
          projectId,
          hours: Number(duration),
          tagIds,
          notes: notes.trim(),
        })
      } else {
        await api.createEntry({
          projectId,
          tagIds,
          date,
          hours: Number(duration),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        })
        // Only a fresh form empties itself. The project is deliberately left
        // selected — the next entry is usually on the same one — but the
        // tags are not, since they describe the work just logged rather
        // than the project.
        setTagIds([])
        setHours('')
        setFrom('')
        setTo('')
        setNotes('')
      }
      setError(null)
      await onLogged(projectId)
    } catch (err) {
      setError(
        err instanceof HttpError
          ? err.message
          : editing
            ? 'Could not save that change.'
            : 'Could not log that time.',
      )
    } finally {
      setSaving(false)
    }
  }

  function chipHoursText(value: number): string {
    if (value === 0.25) return '15 min';
    if (value === 0.5) return '30 min';
    return `${value}h`;
  }

  return (
    <form
      ref={form}
      className={`${heading ? 'card form' : 'form'}${flashing ? ' is-flashing' : ''}`}
      onSubmit={handleSubmit}
    >
      {heading && (
        <div className="card-head">
          <h2>Log time</h2>
          <LogModeMenu mode={mode} onChange={chooseMode} />
        </div>
      )}

      {/* A `div` with a `for` rather than a wrapping `<label>`: the label
          would also toggle the menu on every click inside the control. */}
      <div className="field">
        <label className="field-label" htmlFor={projectFieldId}>
          Project
        </label>
        <ProjectSelect
          id={projectFieldId}
          projects={openProjects}
          value={projectId}
          onChange={chooseProject}
        />
      </div>

      {allowsTags && (
        <div className="field">
          <span className="field-label">Tags (optional)</span>
          <TagPicker tags={openTags} selected={tagIds} onChange={setTagIds} disabled={saving} />
        </div>
      )}

      {mode === 'hours' ? (
        <>
          <label>
            <span>Hours</span>
            <input
              required
              data-autofocus={autoFocus || undefined}
              type="number"
              inputMode="decimal"
              min={INCREMENT_HOURS}
              step={INCREMENT_HOURS}
              max={24}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="1.5"
            />
          </label>

          <div className="quick-hours">
            {QUICK_HOURS.map((value) => (
              <button
                key={value}
                type="button"
                className={`chip${Number(hours) === value ? ' chip-on' : ''}`}
                onClick={() => setHours(String(value))}
              >
                {chipHoursText(value)}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="field">
          <div className="time-pair">
            <label>
              <span>Start</span>
              <input
                required
                data-autofocus={autoFocus || undefined}
                type="time"
                /* Quarter-hour steps, so the browser's own picker offers the
                   only values this app can store. */
                step={INCREMENT_HOURS * 3600}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label>
              <span>End</span>
              <input
                required
                type="time"
                step={INCREMENT_HOURS * 3600}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </div>

          {/* The duration, shown as it is typed. Only this is stored — the
              two times are a way of saying it, not something the entry
              keeps. */}
          {touched && span?.ok && (
            <p className="muted hint time-total">
              That is <strong>{formatHours(span.minutes)}</strong>.
            </p>
          )}
          {touched && span && !span.ok && <p className="error">{span.message}</p>}
        </div>
      )}

      <label>
        <span>Note (optional)</span>
        <textarea
          rows={2}
          maxLength={1000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What you worked on."
        />
      </label>

      {error && <p className="error">{error}</p>}

      {editing ? (
        <div className="row-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {onCancel && (
            <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          {/* Pushed away from Save/Cancel: it is the one button here with a
              consequence beyond this form. */}
          {onDelete && (
            <button
              type="button"
              className="btn-ghost btn-danger btn-end"
              onClick={onDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
        </div>
      ) : (
        <button type="submit" disabled={saving}>
          {saving ? 'Logging…' : `Log time${label ? ` for ${label}` : ''}`}
        </button>
      )}
    </form>
  )
}
